import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import routes from '../../utils/routes';
import { CText, CLoader } from '../../components/common';
import { FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { IoStar } from 'react-icons/io5';
import { handleExpertSearch, Expert } from '../../api';
import {
  HeroSection,
  CopilotTeaser,
  TrendingBuckets,
  SocialProof,
  UpgradeBanner,
  UpgradeModal,
} from '../../components/home';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);
  const subscription = useAppSelector((state) => state.user.subscription);

  const [loading, setLoading] = useState(false);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Get subscription state from Redux
  const subscriptionTier = subscription.tier;
  const queriesRemaining = subscription.queriesRemaining;

  useEffect(() => {
    loadExperts();
  }, []);

  const loadExperts = async () => {
    setLoading(true);
    try {
      const response = await handleExpertSearch('', 1);
      setExperts(response.experts.slice(0, 3)); // Show top 3 experts
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const ExpertCard = ({ expert }: { expert: Expert }) => (
    <div
      className="rounded-2xl p-4 cursor-pointer transition-all duration-200 hover:-translate-y-1 active:scale-98 card-shadow"
      style={{
        backgroundColor: colors.cardBackground || colors.inputBackground,
        border: `1px solid ${colors.borderColor}`,
      }}
      onClick={() => navigate(`/expert/${expert.user_id}`)}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              boxShadow: colors.shadowGlow,
            }}
          >
            {expert.name.charAt(0)}
          </div>
          {expert.user_avg_rating > 0 ? (
            <div className="flex items-center gap-1 mt-2">
              {React.createElement(IoStar as any, { size: 12, fill: '#FFB800', color: '#FFB800' })}
              <CText type="r12" color={colors.textColor} style={{ fontWeight: 600 }}>
                {expert.user_avg_rating.toFixed(1)}
              </CText>
            </div>
          ) : (
            <CText type="r10" color={colors.grayScale500} className="mt-2">
              New
            </CText>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
              {expert.name}
            </CText>
            {expert.is_verified &&
              React.createElement(FiCheckCircle as any, { size: 14, color: colors.primary })}
          </div>
          <CText type="r12" color={colors.grayScale500}>
            {expert.sebi_qualification}
          </CText>
          <div
            className="mt-2 px-2 py-1 rounded-lg inline-block"
            style={{ backgroundColor: colors.primary + '15' }}
          >
            <CText type="r10" color={colors.primary} style={{ fontWeight: 600 }}>
              Rs.{expert.call_session_rate}/hr
            </CText>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-32 lg:pb-24"
      style={{ backgroundColor: colors.bgColor || colors.backgroundColor }}
    >
      <div className="lg:max-w-4xl lg:mx-auto animate-fade-in">
        {/* Hero Section */}
        <HeroSection userName={user?.name} />

        {/* Copilot Teaser */}
        <CopilotTeaser
          queriesRemaining={queriesRemaining}
          subscriptionTier={subscriptionTier}
          onUpgradeClick={handleUpgradeClick}
        />

        {/* Trending Buckets */}
        <TrendingBuckets
          subscriptionTier={subscriptionTier}
          onUpgradeClick={handleUpgradeClick}
        />

        {/* Expert Advisors Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
                Talk to SEBI-Registered Experts
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                Get personalized advice from certified advisors
              </CText>
            </div>
            <button
              onClick={() => navigate(routes.expertSearch)}
              className="flex items-center gap-1 text-sm font-medium transition-all duration-200 hover:translate-x-1"
              style={{ color: colors.primary }}
            >
              View all
              {React.createElement(FiArrowRight as any, { size: 14 })}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <CLoader />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {experts.map((expert) => (
                <ExpertCard key={expert.user_id} expert={expert} />
              ))}
              {/* View More Card */}
              <button
                onClick={() => navigate(routes.expertSearch)}
                className="rounded-2xl p-4 flex flex-col items-center justify-center transition-all duration-200 hover:-translate-y-1 active:scale-98"
                style={{
                  backgroundColor: colors.primary + '10',
                  border: `2px dashed ${colors.primary}50`,
                  minHeight: 120,
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-2"
                  style={{ backgroundColor: colors.primary + '20' }}
                >
                  <CText type="B18" color={colors.primary}>
                    +12
                  </CText>
                </div>
                <CText type="M12" color={colors.primary} style={{ fontWeight: 600 }}>
                  More Experts
                </CText>
              </button>
            </div>
          )}

          {/* Pro Discount Banner */}
          {subscriptionTier === 'free' && (
            <div
              className="mt-4 p-3 rounded-xl flex items-center justify-between"
              style={{
                background: `linear-gradient(135deg, ${colors.primary}15, #9F03F215)`,
                border: `1px solid ${colors.primary}30`,
              }}
            >
              <CText type="r12" color={colors.textColor}>
                First consultation <span style={{ fontWeight: 700 }}>FREE</span> with Pro subscription
              </CText>
              <button
                onClick={handleUpgradeClick}
                className="text-xs font-semibold px-3 py-1 rounded-lg"
                style={{
                  backgroundColor: colors.primary,
                  color: '#fff',
                }}
              >
                Learn more
              </button>
            </div>
          )}
        </div>

        {/* Social Proof */}
        <SocialProof />
      </div>

      {/* Sticky Upgrade Banner (Mobile) */}
      <UpgradeBanner
        subscriptionTier={subscriptionTier}
        onUpgradeClick={handleUpgradeClick}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isVisible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Home;
